/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import clsx from 'clsx';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import { LintSeverity } from '@scale/shared/task_lint/task_linter';
import { Icon, Typography, Tooltip, Markdown, Button, TextArea, Tag } from '@scale/scaleui';
import { motion } from 'framer-motion';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import { useForm, Controller } from 'react-hook-form';
import { usePerStepResponseLinter } from '../hooks/usePerStepResponseLinter/hook';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';

const IconMap: Partial<Record<LintSeverity, IconProp>> = {
  [LintSeverity.ERROR]: 'circle-xmark',
  [LintSeverity.WARNING]: 'exclamation-triangle',
  [LintSeverity.DISMISSABLE_ERROR]: 'circle-xmark',
  [LintSeverity.INFO]: 'info-circle',
};

const IconBgColorMap: Partial<Record<LintSeverity, string>> = {
  [LintSeverity.ERROR]: 'text-danger-700',
  [LintSeverity.WARNING]: 'text-warning-700',
  [LintSeverity.DISMISSABLE_ERROR]: 'text-danger-700',
  [LintSeverity.INFO]: 'text-info-700',
};

const BgColorMap: Partial<Record<LintSeverity, string>> = {
  [LintSeverity.ERROR]: 'bg-danger-50',
  [LintSeverity.WARNING]: 'bg-warning-50',
  [LintSeverity.DISMISSABLE_ERROR]: 'bg-danger-50',
  [LintSeverity.INFO]: 'bg-info-50',
};

const DismissButton: React.FC<{
  feedbackNeeded?: boolean;
  dismissed?: boolean;
  onClick: () => void;
}> = ({ feedbackNeeded, dismissed, onClick }) => {
  return (
    <Tooltip
      text={feedbackNeeded ? 'Rate the linter to dismiss' : 'Dismiss'}
      side="left"
      target={
        <div className="h-2">
          {dismissed ? (
            <Typography.Caption>(dismissed)</Typography.Caption>
          ) : (
            <Icon
              icon={'times-square'}
              variant="danger"
              size="lg"
              onClick={onClick}
              disabled={!!feedbackNeeded}
            />
          )}
        </div>
      }
    />
  );
};

const BinaryFeedbackButton: React.FC<{
  type: 'helpful' | 'not_helpful';
  feedback?: string;
  dismissed?: boolean;
  onClick: () => void;
}> = ({ type, feedback, dismissed, onClick }) => {
  return (
    <Icon
      icon={[feedback === type ? 'fas' : 'far', type === 'helpful' ? 'thumbs-up' : 'thumbs-down']}
      variant="neutral"
      size="sm"
      onClick={onClick}
      disabled={dismissed || feedback === type}
    />
  );
};

const BinaryFeedbackGroup: React.FC<{
  feedback?: string;
  forceLinterFeedback?: boolean;
  dismissed?: boolean;
  handleSubmitFeedback: (type: 'helpful' | 'not_helpful') => void;
}> = ({ feedback, forceLinterFeedback, dismissed, handleSubmitFeedback }) => {
  return (
    <div className="flex pt-4 gap-2 items-center">
      <div>
        <Typography.Caption>
          {forceLinterFeedback
            ? 'Was this linter helpful & intuitive?'
            : 'Was this linter helpful & intuitive? (Optional)'}
        </Typography.Caption>
        {forceLinterFeedback && <Typography.Caption color={'red'}>*</Typography.Caption>}
      </div>
      <div className="flex gap-1 items-center">
        <BinaryFeedbackButton
          type="helpful"
          feedback={feedback}
          dismissed={dismissed}
          onClick={() => handleSubmitFeedback('helpful')}
        />
        <BinaryFeedbackButton
          type="not_helpful"
          feedback={feedback}
          dismissed={dismissed}
          onClick={() => handleSubmitFeedback('not_helpful')}
        />
      </div>
    </div>
  );
};

type RejectFormData = {
  justification: string;
};

const MIN_JUSTIFICATION_LENGTH = 20;

const RejectFeedbackButton: React.FC<{
  dismissed?: boolean;
  needsJustification?: boolean;
  handleSubmitFeedback: (feedback: string) => void;
}> = ({ dismissed, needsJustification, handleSubmitFeedback }) => {
  const [showJustification, setShowJustification] = useState(false);
  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<RejectFormData>();

  if (dismissed) {
    return <div>(Dismissed)</div>;
  }

  return (
    <div className="flex pt-2">
      {showJustification ? (
        <div className="flex flex-col gap-2 w-full">
          <Controller
            name="justification"
            control={control}
            rules={{ required: true, minLength: MIN_JUSTIFICATION_LENGTH }}
            render={({ field }) => (
              <TextArea label={'Explain why this linter is incorrect'} {...field} />
            )}
          />
          {errors.justification?.type === 'required' && (
            <Typography.Paragraph3>Justification is required</Typography.Paragraph3>
          )}
          {errors.justification?.type === 'minLength' && (
            <Typography.Paragraph3>
              Justification must be at least {MIN_JUSTIFICATION_LENGTH} characters long
            </Typography.Paragraph3>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowJustification(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit(data => handleSubmitFeedback(data.justification))}
            >
              Submit Justification
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            if (needsJustification) {
              setShowJustification(true);
            } else {
              handleSubmitFeedback('not_helpful');
            }
          }}
        >
          {`${needsJustification ? 'Reject' : 'Dismiss'} Feedback`}
        </Button>
      )}
    </div>
  );
};

export const LinterPanel = () => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const lintMessages = useChatStoreExtract(state => state.lintMessages);
  const isValidating = useChatStoreExtract(state => state.isPerStepLinterValidating);
  const { setFeedback, dismissError } = usePerStepResponseLinter();
  const [open, setOpen] = useState(true);

  const toggleAnimation = {
    enter: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
      display: 'block',
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.5,
      },
      transitionEnd: {
        display: 'none',
      },
    },
  };

  useEffect(() => {
    if (isValidating) {
      setOpen(true);
    }
  }, [isValidating]);

  function toggleLinterPanel() {
    setOpen(prev => !prev);
  }

  const everyMessageDismissed = lintMessages.every(
    lintMessage =>
      [LintSeverity.WARNING, LintSeverity.DISMISSABLE_ERROR, LintSeverity.INFO].includes(
        lintMessage.severity,
      ) && lintMessage.dismissed,
  );
  const errorCount = lintMessages.filter(
    lintMessage =>
      [LintSeverity.ERROR, LintSeverity.DISMISSABLE_ERROR].includes(lintMessage.severity) &&
      !lintMessage.dismissed,
  ).length;
  const warningCount = lintMessages.filter(
    lintMessage => lintMessage.severity === LintSeverity.WARNING && !lintMessage.dismissed,
  ).length;
  if (lintMessages.length === 0 || everyMessageDismissed) {
    return null;
  }

  return (
    <Draggable
      onStart={() => {
        setIsDragging(true);
      }}
      onStop={() => {
        setIsDragging(false);
      }}
      handle=".chat-linter-panel-handle"
      grid={[25, 25]}
      bounds="body"
    >
      <div
        className={twMerge(
          `chat-linter-panel-handle fixed bottom-[10%] left-[5%] z-20 rounded w-[30%] flex flex-col gap-2 max-h-[50%]`,
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
      >
        <StepCard>
          <StepCard.Header>
            <StepCard.HeaderLeft>
              <StepCard.Icon icon="grip-vertical" />
              <StepCard.HeaderColumn>
                <StepCard.Title>
                  <div className="flex flex-row gap-2">
                    Linter Errors:
                    <Tag variant="danger" icon={['fas', 'circle-xmark']}>{`${errorCount}`}</Tag>
                    <Tag
                      variant="warning"
                      icon={['fas', 'exclamation-triangle']}
                    >{`${warningCount}`}</Tag>
                  </div>
                </StepCard.Title>
              </StepCard.HeaderColumn>
            </StepCard.HeaderLeft>
            <StepCard.HeaderRight>
              <Icon
                icon={open ? 'chevron-up' : 'chevron-down'}
                size="lg"
                onClick={toggleLinterPanel}
              />
            </StepCard.HeaderRight>
          </StepCard.Header>
          {open && (
            <StepCard.Body className="max-h-[400px] overflow-y-scroll">
              <div className="flex flex-col gap-4 my-2">
                {lintMessages.map((lintMessage, index) => {
                  const {
                    severity,
                    dismissed,
                    lintRuleName,
                    linterMessage,
                    lintRuleMessage,
                    forceLinterFeedback,
                    feedback,
                  } = lintMessage;
                  const iconColor = IconBgColorMap[severity];
                  const bgColor = BgColorMap[severity];
                  const icon = IconMap[severity];
                  const feedbackNeeded = forceLinterFeedback && !feedback;

                  return (
                    <motion.div
                      initial="exit"
                      animate={open ? 'enter' : 'exit'}
                      variants={toggleAnimation}
                      key={index}
                      className={clsx('p-4 rounded-lg', bgColor, dismissed && '!opacity-40')}
                    >
                      <div className="flex flex-col gap-2">
                        <div className={clsx('flex justify-between items-center')}>
                          <div className={clsx('flex gap-2 items-center')}>
                            {icon && <FontAwesomeIcon icon={icon} className={iconColor} />}
                            <Typography.Subtitle2>{lintRuleName}</Typography.Subtitle2>
                          </div>
                          {severity === LintSeverity.INFO && (
                            <DismissButton
                              feedbackNeeded={feedbackNeeded}
                              dismissed={dismissed}
                              onClick={() => dismissError(index)}
                            />
                          )}
                        </div>
                        <Markdown markdown={lintRuleMessage} />
                        {linterMessage &&
                          linterMessage
                            .split('\n')
                            .map((line, index) => <Markdown markdown={line} key={index} />)}

                        <div>
                          {[LintSeverity.DISMISSABLE_ERROR, LintSeverity.WARNING].includes(
                            severity,
                          ) ? (
                            <RejectFeedbackButton
                              dismissed={dismissed}
                              needsJustification={severity === LintSeverity.DISMISSABLE_ERROR}
                              handleSubmitFeedback={(feedback: string) => {
                                setFeedback({ index, feedback });
                                dismissError(index);
                              }}
                            />
                          ) : (
                            <BinaryFeedbackGroup
                              feedback={feedback}
                              forceLinterFeedback={forceLinterFeedback}
                              dismissed={dismissed}
                              handleSubmitFeedback={(type: 'helpful' | 'not_helpful') =>
                                setFeedback({ index, feedback: type })
                              }
                            />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </StepCard.Body>
          )}
        </StepCard>
      </div>
    </Draggable>
  );
};
